import { createInertiaApp, router } from '@inertiajs/react'
import { createRoot } from 'react-dom/client'
import type { ComponentType, ReactNode } from 'react'
import type { Page } from '@inertiajs/core'
import type { SharedProps } from '@/types'
import { initPostHog, capturePageview } from '@/lib/posthog'
import { reloadForStaleState, clearStaleReloadGuard } from '@/lib/staleReload'

// When Inertia visits a non-Inertia page (ERB), the server returns HTML
// instead of JSON. By default Inertia shows this in a modal -- instead,
// track the target URL and do a full page visit.
//
// `httpException` also fires for *any* non-Inertia response, including
// 204 No Content and `render json:` from API endpoints (e.g. the
// useDocumentAutosave PATCH). Those callers handle success/failure via
// their own onSuccess/onError, so we must not hard-navigate them — we
// only do the full-page visit when the body is actually HTML.
let pendingVisitUrl: string | null = null

router.on('before', (event) => {
  pendingVisitUrl = event.detail.visit.url.href
})

router.on('httpException', (event) => {
  const response = event.detail.response
  const contentType = (response.headers?.['content-type'] ?? '').toLowerCase()
  const isHtml = contentType.includes('text/html')

  // For non-HTML responses (Inertia JSON 422 validation errors, JSON-API
  // errors from autosave endpoints, 204s) let Inertia's normal handling
  // proceed. It needs to run `setPage` + `onFlash` + `onError` so that
  // page-level flash and form errors actually reach React state.
  // Suppressing the default error dialog isn't necessary either: the dialog
  // only opens for non-Inertia HTML responses (handled in the else branch).
  if (!isHtml) {
    pendingVisitUrl = null
    return
  }

  // HTML response — server rendered an ERB page (e.g. devise sign-in).
  // Inertia would otherwise pop its modal; instead do a full-page navigation.
  event.preventDefault()
  const url = pendingVisitUrl || window.location.href
  pendingVisitUrl = null
  window.location.href = url
})

router.on('finish', (event) => {
  // Only clear when *this* visit is the one we were tracking. A
  // concurrent `router.reload()` poll or a Link prefetch finishing
  // mid-navigation must not erase the target of the user's click.
  if (event.detail.visit.url.href === pendingVisitUrl) {
    pendingVisitUrl = null
  }
})

// Browser-level event for `<link rel="modulepreload">` failures. Vite
// emits these tags for code-split chunks; if the preload 404s after a
// deploy, the browser fires this event. Log the underlying error before
// reloading so anyone watching the console (or pulling logs after the
// reload) can see what actually broke.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  const underlying =
    (event as ErrorEvent).error ??
    (event as CustomEvent).detail ??
    event
  console.error('[vite] modulepreload error — reloading to pick up fresh chunks', underlying)
  reloadForStaleState(pendingVisitUrl)
})

// PostHog SPA pageview tracking — fires on every Inertia client-side
// navigation. PostHog's built-in capture_pageview is disabled in our
// init config so we don't double-fire on the initial load.
router.on('navigate', (event) => {
  capturePageview(event.detail.page.url)
  // A successful navigation proves the page is talking to the server
  // again, so clear the stale-reload guard and let the next drift (if
  // any) get a fresh reload chance.
  clearStaleReloadGuard()
})

interface PageModule {
  default: ComponentType & {
    layout?: (page: ReactNode) => ReactNode
  }
}

// Dynamic imports enable per-page code splitting.
// Each page directory produces its own chunk that is loaded on demand.
const pages = import.meta.glob<PageModule>('../pages/**/*.tsx')

createInertiaApp({
  resolve: async (name) => {
    const loader = pages[`../pages/${name}.tsx`]
    if (!loader) {
      throw new Error(
        `Page not found: ${name}. Available pages: ${Object.keys(pages).join(', ')}`
      )
    }
    try {
      return await loader()
    } catch (err) {
      // Dynamic-import failure after a deploy: the HTML in this tab
      // still references the old chunk filenames and Vite gave us a
      // TypeError whose message mentions the failed module URL. Reload
      // (once) to pick up fresh HTML + chunk hashes.
      if (
        err instanceof TypeError &&
        /dynamically imported module|Failed to fetch/i.test(err.message)
      ) {
        console.error(`[vite] dynamic import failed for page "${name}" — reloading`, err)
        // Hard-load the URL the user was navigating *to*. Inertia hasn't
        // pushed history yet, so reloading the current page would drop
        // them back where they started and the click would look like a
        // no-op. On the initial page load there is no pending visit and
        // this falls back to a plain reload.
        //
        // Only hang the promise when a load is actually in flight; on a
        // second failure within the same session the guard short-
        // circuits, and hanging would strand the user on a forever-
        // loading screen. Falling through re-throws so the boot-failure
        // handler below can show something instead.
        if (reloadForStaleState(pendingVisitUrl)) {
          return new Promise<PageModule>(() => {})
        }
      }
      throw err
    }
  },
  setup({ el, App, props }) {
    const sharedProps = props.initialPage.props as Page<SharedProps>['props']
    const posthogConfig = sharedProps.posthog
    const user = sharedProps.auth?.user ?? null

    initPostHog({
      apiKey: posthogConfig?.api_key,
      apiHost: posthogConfig?.api_host,
      user,
    })

    // Fire an initial pageview for both signed-in and anonymous visitors so
    // sign-in / sign-up flows are captured. The router.on('navigate') handler
    // covers every subsequent SPA navigation.
    if (posthogConfig?.api_key && !user?.admin) {
      capturePageview(window.location.href)
    }

    createRoot(el).render(<App {...props} />)
  },
}).catch((err: unknown) => {
  // We only get here when the initial page component could not be
  // loaded *and* the stale-reload guard refused a second reload (or the
  // failure wasn't a chunk-load error at all). Show something the user
  // can act on instead of a blank page with only a console error.
  console.error('[inertia] failed to mount the initial page', err)
  const el = document.getElementById('app')
  if (!el) return
  el.innerHTML = `
    <div style="max-width:32rem;margin:4rem auto;padding:1.5rem;font-family:system-ui,sans-serif;text-align:center">
      <h1 style="font-size:1.125rem;font-weight:600;margin:0 0 .5rem">This page couldn't load</h1>
      <p style="color:#666;margin:0 0 1rem">A newer version of the app may have just been deployed. Reloading usually fixes it.</p>
      <a href="${window.location.href}" style="display:inline-block;padding:.5rem 1rem;border-radius:.375rem;background:#111;color:#fff;text-decoration:none">Reload page</a>
    </div>`
})
